if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;

          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.bindViewerScroll();
        this.bindDots();

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      isMobileVerticalThumbs() {
        return window.innerWidth < 750 && this.dataset.mobileLayout === 'columns';
      }

      getViewerSlider() {
        return this.elements.viewer.querySelector('.product__media-list');
      }

      getSlides() {
        return Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
      }

      getDots() {
        return Array.from(this.querySelectorAll('.draco-slider-dot'));
      }

      getActiveSlideByScroll() {
        const slides = this.getSlides();
        const slider = this.getViewerSlider();
        if (!slides.length || !slider) return null;

        let bestSlide = slides[0];
        let bestDistance = Infinity;

        const sliderRect = slider.getBoundingClientRect();
        const targetLeft = sliderRect.left + 6;

        slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left - targetLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestSlide = slide;
          }
        });

        return bestSlide;
      }

      bindViewerScroll() {
        const slider = this.getViewerSlider();
        if (!slider || slider.dataset.dracoViewerScrollBound === 'true') return;

        slider.dataset.dracoViewerScrollBound = 'true';

        let raf = null;
        slider.addEventListener(
          'scroll',
          () => {
            if (!this.isMobileVerticalThumbs()) return;

            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
              const activeSlide = this.getActiveSlideByScroll();
              if (!activeSlide) return;

              this.getSlides().forEach((slide) => slide.classList.remove('is-active'));
              activeSlide.classList.add('is-active');

              const thumb = this.elements.thumbnails.querySelector(
                `[data-target="${activeSlide.dataset.mediaId}"]`
              );
              if (thumb) this.setActiveThumbnail(thumb);

              this.updateDotsFromSlide(activeSlide);
            });
          },
          { passive: true }
        );
      }

      bindDots() {
        if (this.dataset.dracoDotsBound === 'true') return;
        this.dataset.dracoDotsBound = 'true';

        const slider = this.getViewerSlider();
        if (!slider) return;

        this.getDots().forEach((dot) => {
          dot.addEventListener('click', () => {
            const mediaId = dot.dataset.mediaId;
            if (!mediaId) return;

            this.setActiveMedia(mediaId, false);
          });
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        if (thumbnail) this.setActiveThumbnail(thumbnail);
        this.updateDotsFromSlide(event.detail.currentElement);
      }

      updateDotsFromSlide(activeSlide) {
        if (!activeSlide) return;

        const mediaId = activeSlide.dataset.mediaId;
        if (!mediaId) return;

        this.getDots().forEach((dot) => {
          const isActive = dot.dataset.mediaId === mediaId;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        this.getSlides().forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: 'smooth',
          });

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.updateDotsFromSlide(activeMedia);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
          element.classList.remove('is-active');
        });

        const button = thumbnail.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', true);
          button.classList.add('is-active');
        }

        const thumbnailsSlider = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailsSlider) return;

        if (this.isMobileVerticalThumbs()) {
          const containerTop = thumbnailsSlider.scrollTop;
          const containerBottom = containerTop + thumbnailsSlider.clientHeight;

          const thumbTop = thumbnail.offsetTop;
          const thumbBottom = thumbTop + thumbnail.offsetHeight;

          if (thumbTop >= containerTop && thumbBottom <= containerBottom) return;

          const targetTop = thumbTop - thumbnailsSlider.clientHeight / 2 + thumbnail.offsetHeight / 2;

          thumbnailsSlider.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}